/* ===========================================================
   CATEGORIES INDEX
   =========================================================== */
import React from "react";
import { ProductCard } from "@/components/product-card";
import { Breadcrumbs, PageHeader, getQuery } from "@/components/shell";
import { NETHRA_DATA } from "@/data/data";
import { Icons } from "@/lib/icons";
import { CATEGORY_IMAGES } from "@/lib/images";
import { useCategories, useCategory, useProducts } from "@/api/hooks";

/** Normalises API CategoryItem (sku_prefix) to the display shape the JSX
 *  uses (sku). Keeps existing markup working without touching field names. */
function normaliseCategory(c) {
  if (!c) return c;
  return {
    ...c,
    sku: c.sku || c.sku_prefix,
    product_count: c.product_count ?? 0,
    description: c.description ?? "",
    glyph: c.glyph || "Pill",
  };
}

/* Bento layout — each tile defines its column/row span on a 4×3 grid.
 * Total: 1 large (2×2) hero + 1 tall (1×2) secondary + 6 regular tiles. */
const BENTO_LAYOUT = {
  prescription:    { col: "1 / span 2", row: "1 / span 2", size: "lg" },
  "cold-chain":    { col: "3",          row: "1 / span 2", size: "md" },
  diagnostics:     { col: "4",          row: "1",          size: "sm" },
  cardio:          { col: "4",          row: "2",          size: "sm" },
  otc:             { col: "1",          row: "3",          size: "sm" },
  surgical:        { col: "2",          row: "3",          size: "sm" },
  wellness:        { col: "3",          row: "3",          size: "sm" },
  "maternal-child":{ col: "4",          row: "3",          size: "sm" },
};

function BentoTile({ cat, layout }) {
  const Icon = Icons[cat.glyph] || Icons.Pill;
  const big = layout.size === "lg";
  const med = layout.size === "md";
  const img = CATEGORY_IMAGES[cat.slug];
  return (
    <a
      href={"/pages/storefront/category.html?slug=" + cat.slug}
      style={{
        gridColumn: layout.col,
        gridRow: layout.row,
        position: "relative",
        overflow: "hidden",
        borderRadius: 16,
        minHeight: big ? 380 : med ? 380 : 200,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        padding: big ? 24 : 18,
        color: "#fff",
        textDecoration: "none",
        background: "#0a0a0a",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        transition: "transform 200ms ease, box-shadow 200ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.18)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
      }}
    >
      {img && (
        <img
          src={img}
          alt={cat.name}
          loading="lazy"
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover",
            transition: "transform 600ms ease",
          }}
        />
      )}
      <span style={{
        position: "absolute", inset: 0,
        background: big
          ? "linear-gradient(180deg, rgba(10,10,10,0) 30%, rgba(10,10,10,0.85) 100%)"
          : "linear-gradient(180deg, rgba(10,10,10,0) 40%, rgba(10,10,10,0.8) 100%)",
        pointerEvents: "none",
      }} />
      <div style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "auto" }}>
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: big ? 44 : 36, height: big ? 44 : 36,
          background: "rgba(255,255,255,0.92)",
          color: "#0a0a0a",
          borderRadius: 10,
        }}>
          <Icon size={big ? 22 : 18} stroke={1.7} />
        </span>
        <span className="mono" style={{
          fontSize: 11,
          padding: "4px 8px",
          background: "rgba(255,255,255,0.18)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.3)",
          borderRadius: 999,
          color: "#fff",
          letterSpacing: 0.5,
        }}>
          {cat.sku}
        </span>
      </div>
      <div style={{ position: "relative", zIndex: 1 }}>
        <div className="mono" style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
          {cat.product_count.toLocaleString("en-IN")} SKUs
        </div>
        <h3 style={{
          margin: 0,
          fontSize: big ? 32 : med ? 24 : 18,
          letterSpacing: "-0.02em",
          lineHeight: 1.15,
          color: "#fff",
        }}>
          {cat.name}
        </h3>
        {(big || med) && (
          <p style={{ margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.5, color: "rgba(255,255,255,0.78)", maxWidth: 480 }}>
            {cat.description}
          </p>
        )}
        <div style={{ marginTop: big ? 16 : 12, display: "flex", alignItems: "center", gap: 6, fontSize: big ? 14 : 12.5, color: "#fff" }}>
          <span>Browse</span>
          <Icons.ArrowR size={big ? 16 : 14} stroke={2} />
        </div>
      </div>
    </a>
  );
}

function CategoriesPage() {
  const { data: catsPage } = useCategories();
  // Fall back to mock list when the API isn't reachable so design review
  // keeps working offline. Otherwise prefer the live data.
  const rawCats = (catsPage?.items?.length ? catsPage.items : NETHRA_DATA.categories);
  const cats = rawCats.map(normaliseCategory);

  // Order tiles per BENTO_LAYOUT (placement is by grid coords, but iteration
  // order doesn't matter — CSS Grid resolves placement).
  const orderedSlugs = Object.keys(BENTO_LAYOUT);
  const orderedCats = orderedSlugs.map((slug) => cats.find((c) => c.slug === slug)).filter(Boolean);

  return (
    <>
      <Breadcrumbs items={[{ label: "Categories" }]} />
      <PageHeader
        eyebrow="Catalog · 8 categories"
        title="Browse our complete healthcare catalog."
        description="From schedule-H prescription stock to cold-chain biologics — every product organized by therapeutic area and fulfillment lane."
        stats={[
          { v: "8",       k: "Categories" },
          { v: "12,400+", k: "Active SKUs" },
          { v: "200+",    k: "Brands" },
        ]}
      />

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div
            className="cat-bento"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gridTemplateRows: "auto auto auto",
              gap: 14,
            }}
          >
            {orderedCats.map((c) => (
              <BentoTile key={c.id} cat={c} layout={BENTO_LAYOUT[c.slug]} />
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .cat-bento { grid-template-columns: repeat(2, 1fr) !important; }
          .cat-bento > a { grid-column: auto !important; grid-row: auto !important; min-height: 220px !important; }
        }
      `}</style>
    </>
  );
}

/* ===========================================================
   CATEGORY DETAIL — hero + subcategory chips + products grid
   (reuses the same Filter sidebar concept but auto-locks category)
   =========================================================== */
const { useState: cdUS, useMemo: cdUM } = React;

function CategoryDetailPage() {
  const slug = getQuery("slug") || "prescription";
  const { data: apiCat } = useCategory(slug);
  const fallbackCat = NETHRA_DATA.categories.find((c) => c.slug === slug) || NETHRA_DATA.categories[0];
  const cat = normaliseCategory(apiCat || fallbackCat);

  const [activeSub, setActiveSub] = cdUS("All");
  const [sort, setSort] = cdUS("popular");
  const Icon = Icons[cat.glyph] || Icons.Pill;
  const catImg = CATEGORY_IMAGES[slug];

  // Server-side: filter by category + optional sub_category, server sorts.
  const apiSort = sort;  // "popular" | "rating" | "price-asc" | "price-desc"
  const { data: pageData, isLoading } = useProducts({
    category: slug,
    sub_category: activeSub !== "All" ? activeSub : undefined,
    sort: apiSort,
    limit: 24,
  });
  const filtered = pageData?.items ?? [];

  // Discover subcategories from the products themselves (one-time wide call).
  // Avoids a separate /categories/{slug}/subcategories endpoint in Phase 2.
  const { data: allInCatPage } = useProducts({ category: slug, limit: 100 });
  const subs = cdUM(() => {
    const set = new Set();
    (allInCatPage?.items ?? []).forEach((p) => {
      if (p.sub_category) set.add(p.sub_category);
    });
    // Stable order: alphabetical
    return Array.from(set).sort();
  }, [allInCatPage]);

  return (
    <>
      <Breadcrumbs items={[
        { label: "Categories", href: "/pages/storefront/categories.html" },
        { label: cat.name },
      ]} />

      <PageHeader
        eyebrow={cat.sku + " · category"}
        title={cat.name}
        description={cat.description + " — explore the full sub-formulary with role-aware pricing and same-day dispatch."}
        image={catImg}
        stats={[
          { v: cat.product_count.toLocaleString("en-IN"), k: "Active SKUs" },
          { v: subs.length || 0, k: "Subcategories" },
          { v: "99.4%", k: "On-time dispatch" },
        ]}
      >
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <a href="#products" className="btn btn-primary">Browse {cat.product_count.toLocaleString("en-IN")} products <Icons.ArrowR size={14} stroke={2} /></a>
          <a href="#" className="btn btn-outline">Download formulary PDF <Icons.Download size={13} stroke={2} /></a>
        </div>
      </PageHeader>

      {subs.length > 0 && (
        <section style={{ padding: "32px 0 0", borderBottom: "1px solid var(--line)" }}>
          <div className="container">
            <div className="kicker" style={{ marginBottom: 10 }}>Subcategories</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingBottom: 24 }}>
              <button className={"filter-radio" + (activeSub === "All" ? " is-active" : "")} onClick={() => setActiveSub("All")}>All</button>
              {subs.map((s) => (
                <button key={s} className={"filter-radio" + (activeSub === s ? " is-active" : "")} onClick={() => setActiveSub(s)}>{s}</button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section" id="products" style={{ paddingTop: 32 }}>
        <div className="container">
          <div className="products-toolbar" style={{ marginBottom: 20 }}>
            <div className="products-count">
              {isLoading
                ? <>Loading…</>
                : <>Showing <strong>{filtered.length}</strong> {activeSub !== "All" ? activeSub : cat.name + " products"}</>}
            </div>
            <div className="toolbar-right">
              <select className="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="popular">Most popular</option>
                <option value="rating">Highest rated</option>
                <option value="price-asc">Price: low to high</option>
                <option value="price-desc">Price: high to low</option>
              </select>
              <a href={"/pages/storefront/products.html?slug=" + slug} className="btn btn-outline btn-sm">Open in catalog <Icons.ArrowR size={13} stroke={2} /></a>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-ic"><Icon size={26} stroke={1.6} /></div>
              <div className="empty-title">No {activeSub} products in stock right now</div>
              <div className="empty-desc">Try switching to a different subcategory, or see our full catalog of {cat.product_count.toLocaleString("en-IN")}+ products in this category.</div>
              <div className="empty-actions">
                <button className="btn btn-primary" onClick={() => setActiveSub("All")}>See all in {cat.name}</button>
              </div>
            </div>
          ) : (
            <div className="prod-grid cols-4">
              {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export { CategoriesPage, CategoryDetailPage };

